'use client'

import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store/auth-store'
import { getMyProfile } from '@/lib/api/profile-routes'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setUser, setSession, setProfile, setLoading } = useAuthStore()
  const accessToken = useAuthStore((s) => s.session?.access_token)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [setUser, setSession, setLoading])

  // Loads the profile (avatar, favourite team, country) once for the whole
  // app on sign-in, instead of leaving it null until the user happens to
  // visit /profile — the navbar needs it on every page.
  useEffect(() => {
    if (!accessToken) {
      setProfile(null)
      return
    }

    let cancelled = false
    const controller = new AbortController()

    getMyProfile(accessToken, controller.signal)
      .then((data) => {
        if (!cancelled) setProfile(data)
      })
      .catch(() => {
        // Navbar just falls back to the generic icon — no need to surface this.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [accessToken, setProfile])

  return <>{children}</>
}
